import React from 'react'
// hooks
import { useSnackbar } from 'notistack'
import { useFileUpload } from 'hooks/useFileUpload'
// types
import { AssetType } from 'types/graphql'
// utils
import { fileToBase64 } from 'utils/upload'

export type Image = { name: string; url: string }

export const useImageUpload = ({
  onUpload,
  assetType,
  entityPrefix
}: {
  onUpload?: (image: Image | any) => void
  assetType: AssetType
  entityPrefix: string
}) => {
  const [image, setImage] = React.useState<Image | null>(null)

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const { upload } = useFileUpload()

  const onFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      event.stopPropagation()
      event.preventDefault()

      const file = event.target.files[0]

      enqueueSnackbar('   Uploading...', {
        key: 'upload',
        variant: 'success',
        autoHideDuration: null,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        }
      })

      const isMsg = file.type === 'application/vnd.ms-outlook' || file.name.toLowerCase().endsWith('.msg')
      const contentType = isMsg ? 'application/vnd.ms-outlook' : file.type

      if (!navigator.onLine) {
        const base64file = await fileToBase64(file)

        onUpload?.({
          assetType,
          contentType,
          entityPrefix,
          file: base64file,
          fileName: file.name
        })

        return
      }

      const downloadUrl = await upload(file, {
        file: {
          assetType,
          contentType,
          entityPrefix,
          originalFileName: file.name
        }
      })

      const image = { name: file.name, url: downloadUrl }

      setImage(image)
      onUpload?.(image)
    } catch (err) {
      console.error('[useImageUpload.onFileUpload]', err)
    } finally {
      event.target.value = ''
      closeSnackbar('upload')
    }
  }

  return { image, onFileUpload }
}
